import React, { useState, useRef, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import i18n from 'i18next'
import Anchor from './Anchor'

import './styles/hoverInfo.scss'

const RIGHT_EDGE_MIN_MARGIN = 16 // from the right edge of viewport

function getPortalTransform(dir, rect) {
    const defaultBottom = `translate(${rect.left - 28}px, ${rect.bottom + 12}px)`
    switch (dir) {
        // for usePortal width is fixed at 400px
        case 'bottom': return defaultBottom
        case 'left': return `translate(${rect.left - 408}px, ${rect.top - 14}px)`
        // case 'top': // TODO: requires pre-rendering the tooltip to know its height
        case 'right': return `translate(${rect.right + 8}px, ${rect.top - 14}px)`
        default: return defaultBottom
    }
}

function HoverInfo(props) {
    const {
        children, cssClass, title, desc, linkText, href, isExternal, target, dir, usePortal,
    } = props

    const containerRef = useRef(null)
    const tooltipRef = useRef(null)
    const [adjustedDir, setAdjustedDir] = useState(dir || '')
    const [isHovered, setIsHovered] = useState(false)

    useLayoutEffect(() => {
        const checkPosition = () => {
            if (!containerRef.current || !tooltipRef.current) return

            const containerBoundary = containerRef.current.getBoundingClientRect()
            const tooltipWidth = tooltipRef.current.offsetWidth
            const viewportWidth = window.innerWidth
            const distanceFromRight = viewportWidth - containerBoundary.right

            const threshold = tooltipWidth - RIGHT_EDGE_MIN_MARGIN
            switch (dir) {
                case 'bottom':
                    if (distanceFromRight < threshold) setAdjustedDir('bottom-left')
                    break
                default:
                    setAdjustedDir(dir || '')
                    break
            }
        }
        window.addEventListener('resize', checkPosition)
        const container = containerRef.current
        if (container) {
            container.addEventListener('mouseenter', checkPosition)
            checkPosition()
        }

        return () => {
            if (container) container.removeEventListener('mouseenter', checkPosition)
            window.removeEventListener('resize', checkPosition)
        }
    }, [dir])

    const getPortalStyle = () => {
        if (!containerRef.current) return {}
        const rect = containerRef.current.getBoundingClientRect()
        return {
            position: 'fixed',
            top: 0,
            left: 0,
            margin: 0,
            visibility: 'visible',
            zIndex: 9999,
            transform: getPortalTransform(adjustedDir, rect),
        }
    }

    const tooltip = (
        <div
            className={`hover-info-wrapper ${adjustedDir}${usePortal ? ' hover-info-portalled' : ''}`}
            ref={tooltipRef}
            style={usePortal ? getPortalStyle() : undefined}
        >
            {title && <p className="hover-title">{title}</p>}
            <p className="hover-desc" dangerouslySetInnerHTML={{ __html: desc }} />
            {href && (
                <div className="link-wrapper">
                    <Anchor
                        cssClass="read-link"
                        href={href}
                        target={target || '_blank'}
                        isExternal={isExternal || true}
                    >
                        {linkText || i18n.t('read_more', 'Read more')}
                        <i className="fa-sharp fa-regular fa-arrow-right" />
                    </Anchor>
                </div>
            )}
        </div>
    )

    return (
        <React.Fragment>
            <div
                className={`hover-info-container ${cssClass}`}
                ref={containerRef}
                onMouseEnter={usePortal ? () => setIsHovered(true) : undefined}
                onMouseLeave={usePortal ? () => setIsHovered(false) : undefined}
            >
                <div className="hover-info-target">
                    {children}
                </div>
                {!usePortal && tooltip}
            </div>
            {usePortal && isHovered && createPortal(tooltip, document.body)}
        </React.Fragment>
    )
}

export default HoverInfo
